import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOxbloodBtnSx } from './PartnerListChrome';
import { SingleImageUploader } from './ImageUploader';
import {
  INVENTORY_PRICE_FREQUENCIES,
  normalizeInventoryPriceFrequency,
} from '../utils/inventoryPrice';
import { CATALOG_CONFIGS } from '../utils/partnerCatalog';

const PARTNER_AUTH = { usePartnerAuth: true };

export const catalogFieldSx = {
  '& .MuiInput-underline:before': { borderBottomColor: DOCUMENT.ruleHard },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInput-underline:after': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInputLabel-root': { color: DOCUMENT.ink3 },
  '& .MuiInputLabel-root.Mui-focused': { color: DOCUMENT.accent },
};

const formFromItem = (config, item) => ({
  name: item?.name || '',
  category: item?.category || config.defaultCategory,
  description: item?.description || '',
  imageUrl: item?.imageUrl || item?.image_url || '',
  defaultPrice: item ? String(item.defaultPrice ?? '') : '',
  priceFrequency: normalizeInventoryPriceFrequency(item?.priceFrequency ?? item?.price_frequency),
  currency: item?.currency || 'USD',
  capacity: item?.capacity != null ? String(item.capacity) : '',
  notes: item?.notes || '',
  paymentLinkUrl: item?.paymentLinkUrl || item?.payment_link_url || '',
  paymentLinkLabel: item?.paymentLinkLabel || item?.payment_link_label || '',
});

const payloadFromForm = (config, form) => {
  const payload = {
    name: form.name.trim(),
    category: form.category,
    description: form.description.trim(),
    imageUrl: form.imageUrl.trim() || null,
    defaultPrice: Number(form.defaultPrice),
    priceFrequency: normalizeInventoryPriceFrequency(form.priceFrequency),
    currency: form.currency || 'USD',
    notes: form.notes.trim(),
    paymentLinkUrl: form.paymentLinkUrl.trim(),
    paymentLinkLabel: form.paymentLinkLabel.trim(),
  };
  if (config.showCapacity) {
    payload.capacity = form.capacity === '' ? null : Number(form.capacity);
  }
  return payload;
};

/**
 * Create or edit an inventory item / add-on. Saves through the partner catalog
 * API and hands the saved item to `onSaved`. `renderExtra(form)` adds fields
 * below the form (for example trip delivery options).
 */
const PartnerCatalogItemDialog = ({
  open,
  kind,
  item = null,
  onClose,
  onSaved,
  title,
  saveLabel,
  renderExtra,
}) => {
  const config = CATALOG_CONFIGS[kind];
  const { t } = useTranslation();
  const [form, setForm] = useState(() => formFromItem(config, item));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(formFromItem(config, item));
      setError('');
    }
  }, [open, config, item]);

  const copy = (key) => t(`${config.i18n}.${key}`);
  const testId = (suffix) => `${config.testIdPrefix}-${suffix}`;
  const setField = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      const payload = payloadFromForm(config, form);
      const response = item
        ? await api.patch(`${config.endpoint}/items/${item.id}`, payload, PARTNER_AUTH)
        : await api.post(`${config.endpoint}/items`, payload, PARTNER_AUTH);
      await onSaved?.(response.data.item);
    } catch (err) {
      setError(err.response?.data?.error || copy('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { bgcolor: DOCUMENT.surface, borderRadius: 0 } }}>
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400 }}>
        {title || (item ? copy('editItem') : copy('newItem'))}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <TextField
          fullWidth
          margin="normal"
          label={t('partnerInventory.fieldName')}
          value={form.name}
          onChange={setField('name')}
          variant="standard"
          sx={catalogFieldSx}
          inputProps={{ 'data-testid': testId('field-name') }}
        />
        <Box sx={{ mt: 2.5, mb: 1 }} data-testid={testId('image-uploader')}>
          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mb: 1 }}>
            {t('partnerInventory.fieldImage')}
          </Typography>
          <SingleImageUploader
            value={form.imageUrl || ''}
            onChange={(url) => setForm((prev) => ({ ...prev, imageUrl: url || '' }))}
            preset="gallery"
            folder={config.imageFolder}
            label={t('partnerInventory.fieldImageBrowse')}
            height={160}
            cropShape="rect"
            usePartnerAuth
          />
        </Box>
        <TextField
          select
          fullWidth
          margin="normal"
          label={t('partnerInventory.fieldCategory')}
          value={form.category}
          onChange={setField('category')}
          variant="standard"
          sx={catalogFieldSx}
        >
          {config.categories.map((cat) => (
            <MenuItem key={cat} value={cat}>{t(`${config.i18n}.category.${cat}`)}</MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          margin="normal"
          label={t('partnerInventory.fieldPrice')}
          value={form.defaultPrice}
          onChange={setField('defaultPrice')}
          variant="standard"
          sx={catalogFieldSx}
          inputProps={{ 'data-testid': testId('field-price') }}
        />
        <TextField
          select
          fullWidth
          margin="normal"
          label={t('partnerInventory.fieldPriceFrequency')}
          value={form.priceFrequency || 'one_time'}
          onChange={(e) => setForm((prev) => ({
            ...prev,
            priceFrequency: normalizeInventoryPriceFrequency(e.target.value),
          }))}
          variant="standard"
          sx={catalogFieldSx}
          inputProps={{ 'data-testid': testId('price-frequency') }}
        >
          {INVENTORY_PRICE_FREQUENCIES.map((freq) => (
            <MenuItem key={freq} value={freq}>
              {t(`partnerInventory.frequency.${freq}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          margin="normal"
          label={t('partnerInventory.fieldCurrency')}
          value={form.currency}
          onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase().slice(0, 3) }))}
          variant="standard"
          sx={catalogFieldSx}
        />
        {config.showCapacity ? (
          <TextField
            fullWidth
            margin="normal"
            label={t('partnerInventory.fieldCapacity')}
            value={form.capacity}
            onChange={setField('capacity')}
            variant="standard"
            sx={catalogFieldSx}
          />
        ) : null}
        <TextField
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          label={t('partnerInventory.fieldDescription')}
          value={form.description}
          onChange={setField('description')}
          variant="standard"
          sx={catalogFieldSx}
        />
        <TextField
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          label={t('partnerInventory.fieldNotes')}
          value={form.notes}
          onChange={setField('notes')}
          variant="standard"
          sx={catalogFieldSx}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('partnerInventory.fieldPaymentLink')}
          placeholder="https://"
          value={form.paymentLinkUrl}
          onChange={setField('paymentLinkUrl')}
          variant="standard"
          sx={catalogFieldSx}
          helperText={t('partnerInventory.paymentLinkHint')}
          inputProps={{ 'data-testid': testId('field-payment-link') }}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('partnerInventory.fieldPaymentLinkLabel')}
          value={form.paymentLinkLabel}
          onChange={setField('paymentLinkLabel')}
          variant="standard"
          sx={catalogFieldSx}
          inputProps={{ 'data-testid': testId('field-payment-link-label') }}
        />
        {renderExtra ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
            {renderExtra(form)}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          sx={partnerOxbloodBtnSx}
          disabled={saving || !form.name.trim() || !(Number(form.defaultPrice) >= 0)}
          onClick={save}
          data-testid={testId('save')}
        >
          {saving ? t('common.saving') : (saveLabel || t('common.save'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerCatalogItemDialog;
